import { Component, EventEmitter, Inject, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { FormControl } from '@angular/forms';
import orderBy from 'lodash-es/orderBy';
import { storageKey } from 'libs/core/src/app.const';
import { BasketPageModel } from 'libs/core/src/lib/model/page/basket/basket.page.model';
import { CateringAggregationArticleViewModel } from 'libs/core/src/lib/model/catering/catering-aggregation.view.model';
import { ScreenTag } from 'libs/core/src/lib/model/screen-tag.model';
import { IVoucher, OrderViewModel } from 'libs/core/src/lib/model/view-model/order/order.view.model';
import { ScreeningItemViewModel } from 'libs/core/src/lib/model/view-model/order/screening-item/screening-item.view.model';
import { ScreeningViewModel } from 'libs/core/src/lib/model/view-model/screening/screening.view.model';
import { VoucherTypeModel } from 'libs/core/src/lib/model/voucher-type.model';
import { AppService } from 'libs/core/src/lib/service/app.service';
import { DateTimeService } from 'libs/core/src/lib/service/datetime.service';
import { MessageService } from 'libs/core/src/lib/service/message.service';
import { SeatTranslatorService } from 'libs/core/src/lib/service/seat/seat-translator.service';
import { TotalizerService } from 'libs/core/src/lib/service/totalizer.service';
import { VirtualCinemaService } from 'libs/core/src/lib/service/virtual-cinema/virtual-cinema.service';
import { VoucherService } from 'libs/core/src/lib/service/voucher.service';
import { OrderStateService } from 'libs/core/src/lib/state/order.state.service';
import { TicketViewModel } from 'libs/core/src/lib/model/view-model/shared/ticket/ticket.view.model';
import { MessageModel, MessageType } from 'libs/core/src/lib/model/message.model';
import { ENVIRONMENT_TOKEN, ErrorHandlerService, LocationStateService, RecalculateOperationType } from 'libs/core/src/public-api';
import { ScreenViewModel } from 'libs/core/src/lib/model/view-model/screen/screen.view.model';
import { SeatViewModel } from 'libs/core/src/lib/model/view-model/screen/seat/seat.view.model';
import { OrderSummaryViewModel } from 'libs/core/src/lib/model/page/order/order-summary.view.model';
import { OrderSummaryBuilder } from 'libs/core/src/lib/model/page/order/order-summary.builder';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import { debounceTime, filter, of, Subject, Subscription, switchMap, tap } from 'rxjs';
import { LoaderService } from 'libs/core/src/lib/service/loading.service';
import { LoaderEnum } from 'libs/core/src/lib/enum/loader.enum';
import { OrderDataProvider } from 'libs/core/src/lib/data-provider/order.data-provider';
import { OrderSummaryGiftCardItemViewModel } from 'libs/core/src/lib/model/page/order/order-summary-gift-card-item.view.model';
import { PaymentMethodViewModel } from 'libs/core/src/lib/model/view-model/order/payment-method/payment-method.view.model';
import { UseCardRequestModel } from 'libs/core/src/lib/model/request/use-card.request.model';
import { CardDataProvider } from 'libs/core/src/lib/data-provider/card.data-provider';
import { FlowService } from 'libs/core/src/lib/service/flow.service';
import { FlowType } from 'libs/core/src/lib/model/component/steps/flow-type.model';
import { RecaptchaComponent } from '@lib/recaptcha';
import { ExtraFeeModel } from '../../basket/basket-fee/model/basket.fee.model';
import groupBy from 'lodash-es/groupBy';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

interface ItemGroup {
  name: string;
  count: number;
  graphicUrl: string;
}

@UntilDestroy()
@Component({
  template: ''
})
export class OrderSummaryComponent implements OnInit {
  public orderSummaryView: OrderSummaryViewModel = null;
  public today = DateTime.local();
  public isDream = false;
  public voucherForm = new FormControl();
  public isLoaded = false;

  public taxId: string;
  public voucherErrorMessage: string = null;
  public screenTag: ScreenTag;
  public modalRef: BsModalRef;

  trackByGroupName = (_: number, g: ItemGroup) => g.name;

  captchaResponse: string = null;
  @ViewChild('captchaRef') captchaRef: RecaptchaComponent;

  @Input()
  public set screen(screen: ScreenViewModel) {
    this._screen = screen;
  }

  public get screen(): ScreenViewModel {
    return this._screen;
  }

  @Input() public isLastPage = false;
  @Input() buttonText: string;
  @Input() showSeatsByTicketType = false;
  @Input() public footerTemplate: TemplateRef<any> = null;
  @Input() public parentContext = '';
  @Input() public voucherError: string = null;
  @Input() displaySubmitButton = true;
  @Input() showSimpleTicketSummary = false;
  @Input() showCaptcha = false;
  @Input() pickupTime: string = null;

  @Output() nextClickButtonEvent = new EventEmitter();
  @Output() loadingEvent = new EventEmitter();
  @Output() checkVoucherItem = new EventEmitter();

  private _screen: ScreenViewModel = null;
  private _screening: ScreeningViewModel = null;
  private _groupedSeats = [];
  private _showSubtitlesLabel = false;
  private _vouchers: IVoucher[];

  flowType: FlowType;
  flowTypeEnum: typeof FlowType = FlowType;

  protected buildOrderSummaryViewDebouncer: Subject<void> = new Subject<void>();

  public countsByFeeId = new Map<string, number>();

  optionalExtraFeesWithQuantity: ExtraFeeModel[] = [];
  valueToPay = 0;

  constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    protected translateService: TranslateService,
    protected virtualCinemaService: VirtualCinemaService,
    protected totalizerService: TotalizerService,
    protected orderStateService: OrderStateService,
    protected appService: AppService,
    protected voucherService: VoucherService,
    protected messageService: MessageService,
    protected dateTimeService: DateTimeService,
    protected modalService: BsModalService,
    protected loaderService: LoaderService,
    protected orderDataProvider: OrderDataProvider,
    protected cardDataProvider: CardDataProvider,
    protected locationStateService: LocationStateService,
    protected flowService: FlowService,
    protected orderSummaryBuilder: OrderSummaryBuilder,
    protected errorHandlerService: ErrorHandlerService
  ) {
    this.flowType = flowService.FlowType;
    this.buildOrderSummaryViewDebouncer
      .pipe(
        debounceTime(100),
        switchMap(() => {
          if (this.flowType !== FlowType.Voucher) {
            return this.orderDataProvider.getTickets(this.order?.cinemaId, this.order?.id).pipe(
              tap((tickets) => {
                this.ticketList = tickets;
              })
            );
          }
          return of(null);
        })
      )
      .subscribe(() => this.buildOrderSummaryView());
  }

  private _order: OrderViewModel = null;
  get order(): OrderViewModel {
    return this._order;
  }

  @Input()
  set order(value: OrderViewModel) {
    this._order = value;
    if (value?.taxId) {
      this.taxId = value.taxId;
    }

    this.buildOrderSummaryViewDebouncer.next();
  }

  ticketList: TicketViewModel[] = [];
  screeningId: string | null;

  private _selectedVoucherList: Array<VoucherTypeModel> = null;
  get selectedVoucherList(): Array<VoucherTypeModel> {
    return this._selectedVoucherList;
  }

  @Input()
  set selectedVoucherList(value: Array<VoucherTypeModel>) {
    this._selectedVoucherList = value;
    this.buildOrderSummaryViewDebouncer.next();
  }

  private _selectedCateringArticleCombinationList: Array<CateringAggregationArticleViewModel> = null;
  get selectedCateringArticleCombinationList(): Array<CateringAggregationArticleViewModel> {
    return this._selectedCateringArticleCombinationList;
  }

  @Input()
  set selectedCateringArticleCombinationList(value: Array<CateringAggregationArticleViewModel>) {
    this._selectedCateringArticleCombinationList = value;
    this.buildOrderSummaryViewDebouncer.next();
  }

  @Input()
  set basketPageModel(value: BasketPageModel | null) {
    if (!value) {
      return;
    }

    this._screen = value.screen;
    this._screening = value.screening;
    this.screeningId = value.screening?.id ?? null;
    this.ticketList = value.tickets;
    this.buildOrderSummaryViewDebouncer.next();
  }

  @Input() public summaryPage = false;

  ngOnInit() {
    this.isDream = 'true' === localStorage.getItem(storageKey.isDream);
    this.setScreenTag();
    this.hasSubtitles();
    this.simpleTicketsData();
    this.loadingEvent.emit(true);

    this.orderStateService.state$
      .pipe(
        filter((order) => !!order),
        tap((order) => {
          this.order = order;
          if (order?.taxId) {
            this.taxId = order.taxId;
          }

          this.buildOrderSummaryViewDebouncer.next();
        }),
        untilDestroyed(this)
      )
      .subscribe();

    this.orderStateService.voucher$
      .pipe(
        tap((data) => {
          if (this.voucherForm && !(data && data.length && this.voucherForm)) {
            this.voucherForm.reset();
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  simpleTicketsData() {
    if (!this.summaryPage) {
      const seats = this.getSeats(this.order?.screeningItems);
      if (seats) {
        this._groupedSeats = seats;
      }
    }
  }

  getSeatsByTicketId(ticketId: string, vouchered: boolean = false) {
    const orderItemsByTicketId = this.order.screeningItems.filter((c) => c.ticketId === ticketId && c.hasVoucher() === vouchered);
    return this.getSeats(orderItemsByTicketId);
  }

  nextButtonClick() {
    if (this.captchaRef && !this.checkCaptcha()) {
      this.captchaRef.execute();
      return;
    }

    this.nextClickButtonEvent.emit();
  }

  checkCaptcha() {
    return this.captchaResponse && this.captchaResponse.length > 0;
  }

  captchaResolved(captchaResponse: string) {
    this.captchaResponse = captchaResponse;
    if (this.checkCaptcha()) {
      this.nextButtonClick();
    }
  }

  public getScreenName(): string {
    return this.orderSummaryView?.screen?.name?.toUpperCase().replace('- DREAM', '');
  }

  public get totalPrice(): number {
    return (this.orderSummaryView?.totalPrice ?? 0) + this.getTotalOptionalExtraFee();
  }

  getTotalOptionalExtraFee(): number {
    let sum = 0.0;
    this.orderSummaryView?.optionalExtraFees.forEach((item) => {
      sum += item.price * this.order.getCountOptionalFeeByGroupFee(item.id);
    });

    return sum;
  }

  hasSubtitles(): void {
    if (this.orderSummaryView?.screen != null) {
      this.orderSummaryView.screen.movieSubtitles.forEach((s) => {
        if (s.length > 0) {
          this._showSubtitlesLabel = true;
        }
      });
    }
  }

  submitVoucherForm(): void {
    const voucherNumber = this.voucherForm.value;
    this.voucherForm.reset();
    this.checkVoucherItem.emit(voucherNumber);
  }

  public buildOrderSummaryView(): void {
    if (!this.order) {
      this.orderSummaryView = null;
      return;
    }

    const order = this.order;
    if (!this.pickupTime) {
      let pickupTime: DateTime;

      if (order.pickupTime) {
        pickupTime = order.pickupTime;
      } else if (order.fbItems?.length > 0) {
        const cateringItem = order.fbItems.find((item) => Boolean(item.pickupTime));

        if (cateringItem) {
          pickupTime = cateringItem.pickupTime;
        }
      }

      this.pickupTime = pickupTime ? pickupTime.toFormat('hh:mm a') : null;
    }

    if (order && order.screeningItems) {
      const hasVouchers = order.screeningItems.some((it) => it.hasVoucher());
      if (!hasVouchers) {
        this.voucherForm.reset();
      }
    }

    const selectedVoucherList: Array<VoucherTypeModel> = this._selectedVoucherList ? this._selectedVoucherList : [];
    const selectedCateringArticleCombinationList: Array<CateringAggregationArticleViewModel> = this._selectedCateringArticleCombinationList
      ? this._selectedCateringArticleCombinationList.sort((a, b) => {
          return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
        })
      : new Array<CateringAggregationArticleViewModel>();

    const orderSummaryView = this.orderSummaryBuilder.getOrderSummary(
      order,
      this.ticketList,
      selectedVoucherList,
      selectedCateringArticleCombinationList,
      this.screeningId,
      this._screen,
      this._screening,
      this.useOrderItemPrice()
    );

    this.orderSummaryView = orderSummaryView;

    this.rebuildCounts(order.screeningItems);
    this.optionalExtraFeesWithQuantity = this.prepareOptionalExtraFeeList(order, orderSummaryView);

    this.totalizerService.changeTicketPriceEmit((this.orderSummaryView?.items ?? []).map((item) => item.ticket));
  }

  protected useOrderItemPrice(): boolean {
    return false;
  }

  private prepareOptionalExtraFeeList(order, orderSummaryView) {
    if (!order?.screeningItems?.length) return [];

    const extraFeeTag = (id: string) => orderSummaryView?.optionalExtraFees?.find((item) => item.id === id)?.tag;
    const fees: ExtraFeeModel[] = [];
    for (const screeningItem of order.screeningItems) {
      if (!screeningItem.optionalExtraFees?.length) continue;
      const extraFees = screeningItem.extraFees ?? [];
      for (const feeId of screeningItem.optionalExtraFees) {
        for (const fee of extraFees) {
          if (fee.id === feeId) {
            fees.push(new ExtraFeeModel({ ...fee, tag: extraFeeTag(fee.id) }));
          }
        }
      }
    }

    const grouped = groupBy(fees, (fee) => `${fee.id}_${fee.price}`);
    return Object.values(grouped).map((group) => {
      const base = group[0];
      const quantity = group.reduce((sum, fee) => sum + (fee.quantity ?? 0), 0);
      return { ...base, quantity };
    });
  }

  private rebuildCounts(screeningItems) {
    const map = new Map<string, number>();
    for (const item of screeningItems) {
      for (const feeId of item.optionalExtraFees ?? []) {
        map.set(feeId, (map.get(feeId) ?? 0) + 1);
      }
    }
    this.countsByFeeId = map;
  }

  private getSeats(orderItems: ScreeningItemViewModel[]) {
    if (!this._screen) {
      return [];
    }

    const seats: { key: string; value: SeatViewModel[] }[] = [];
    orderItems?.forEach(({ seatId }) => {
      if (!seatId) {
        return;
      }

      const seat = this._screen.findSeatById(seatId);
      const seatsIncludeKey = seats.find((c) => c.key === seat.rowNumber);
      if (!seatsIncludeKey) {
        seats.push({ key: seat.rowNumber, value: [seat] });
      } else {
        seatsIncludeKey.value.push(seat);
      }
    });

    const skippingRomanDigitsInRows = SeatTranslatorService.isNotRomanDigit(seats.map((elem) => elem.value[0].rowNumber));
    const skippingRomanDigitsInCols = SeatTranslatorService.isNotRomanDigit(seats.map((elem) => elem.value[0].legendCol));

    seats.forEach((elem) => {
      elem.value.forEach((val) => {
        val.translateRow = SeatTranslatorService.translateValue(val.rowNumber, skippingRomanDigitsInRows);
        val.translateCol = SeatTranslatorService.translateValue(val.legendCol, skippingRomanDigitsInCols);
      });
      elem.value = orderBy(elem.value, ['translateRow', 'translateCol']);
    });

    return seats.sort(({ key: aKey }, { key: bKey }) => parseInt(aKey) - parseInt(bKey));
  }

  public get summaryText(): string {
    return this.orderSummaryView.orderVoucherItems.length > 0 ? 'order.ticketsSummary.grandTotal' : 'order.ticketsSummary.totalBrutto';
  }

  private setScreenTag() {
    if (!this.screen) {
      return;
    }

    this.screenTag = {
      tags: this.screen.movieTags,
      premiereDate: this.screen.moviePremiere,
      screeningDate: this.screen.screeningTimeFrom
    };
  }

  public useVoucher() {
    this.isLoaded = false;
    this.voucherError = null;
    let isFetched = false;
    const order = this.orderStateService.getOrder();
    if (!order) {
      //TODO add error
      return;
    }

    return this.voucherService.assignVoucherToOrder(order.cinemaId, order.id, this.voucherForm.value).subscribe({
      next: (order) => {
        this._vouchers = this.orderStateService.getVouchers();
        if (isFetched) {
          this.isLoaded = true;
        }
      },
      error: (e) => {
        const error = this.errorHandlerService.getError(e);
        if (error) {
          this.voucherError = error.message;
          this.prepareError(e.error.error.code, MessageType.danger);
        }

        if (isFetched) {
          this.isLoaded = true;
        }
      }
    });
  }

  private prepareError(err: number, type: MessageType): void {
    this.messageService.add(new MessageModel(type, this.translateService.instant(`errors.${err}`), err.toString()));
  }

  public removeGroupOfCards(card: OrderSummaryGiftCardItemViewModel) {
    this.loaderService.showLoader(LoaderEnum.MAIN);

    const order = this.orderStateService.getOrder();
    this.orderDataProvider
      .removeItem(order.cinemaId, order.id, card.id)
      .pipe(switchMap(() => this.orderDataProvider.getOrder(order.cinemaId, order.id)))
      .subscribe({
        next: (res) => {
          this.orderStateService.setOrder(res);
          this.loaderService.hideLoader(LoaderEnum.MAIN);
        },
        error: (e) => {
          this.loaderService.hideLoader(LoaderEnum.MAIN);
        }
      });
  }

  removeInternalPaymentMethod(paymentMethod: PaymentMethodViewModel): void {
    const order = this.orderStateService.getOrder();
    if (!paymentMethod || !order || !order.id || !order.cinemaId) {
      return;
    }

    this.loaderService.showLoader(LoaderEnum.LOYALTY);

    const request = order.paymentMethods
      .filter((x) => x.cardId && x.cardId !== paymentMethod.cardId)
      .map((x) => new UseCardRequestModel(x.id, paymentMethod.id));

    this.cardDataProvider.useCards(order.cinemaId, order.id, request).subscribe({
      next: (res) => {
        this.orderStateService.setOrder(res);
        this.loaderService.hideLoader(LoaderEnum.LOYALTY);
      },
      error: (error) => {
        this.loaderService.hideLoader(LoaderEnum.LOYALTY);
      }
    });
  }

  removeUsedVoucher(voucher: IVoucher) {
    const order = this.orderStateService.getOrder();
    if (!voucher || !order || !order.id || !order.cinemaId) {
      return;
    }

    this.loaderService.showLoader(LoaderEnum.LOYALTY);
    this.voucherService
      .removeVoucherFromOrder(order.cinemaId, order.id, voucher.number)
      .pipe(switchMap(() => this.orderStateService.recalculateIfNeeded(this.orderStateService.getOrder(), RecalculateOperationType.RECALCULATE)))
      .subscribe({
        next: (updatedOrder) => {
          this.orderStateService.setOrder(updatedOrder);
          this.loaderService.hideLoader(LoaderEnum.LOYALTY);
        },
        error: () => {
          this.loaderService.hideLoader(LoaderEnum.LOYALTY);
        }
      });
  }

  public getCinemaName(translateDefaultCinemaName: boolean): string {
    const getCinemaNameByCinemaId = () => {
      return this.locationStateService.getCinemaNameByCinemaId(this.order?.cinemaId ?? '') ?? '';
    };

    let defaultCinemaName: string =
      translateDefaultCinemaName && this.order?.cinemaId
        ? this.translateService.instant(getCinemaNameByCinemaId().toTranslationKey('screen.cinema'))
        : getCinemaNameByCinemaId();

    if (defaultCinemaName === getCinemaNameByCinemaId().toTranslationKey('screen.cinema')) {
      defaultCinemaName = getCinemaNameByCinemaId();
    }

    return this.virtualCinemaService.getVirtualCinemaName([{ screenId: this.screen?.id }], defaultCinemaName);
  }

  phoneNumberFormat(phone: string): string {
    return phone
      .replace(/[^\dA-Z]/g, '')
      .replace(/(.{3})/g, '$1 ')
      .trim();
  }

  groupByName(items: any[]): ItemGroup[] {
    const map = new Map<string, ItemGroup>();

    for (const it of items) {
      const key = it.name?.trim() || '—';
      const quantity = it.quantity;
      if (!map.has(key)) map.set(key, { name: key, count: 0, graphicUrl: it.graphicUrl });
      const g = map.get(key)!;
      g.count += quantity;
    }

    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }
}
